import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Download, ChevronRight, ChevronDown, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR } from "@/lib/constants";

/**
 * IncomeStatementReport — 2026-10-18 (user request P2).
 *
 * Laba Rugi akrual. Akar perhitungan di backend
 * (`/api/finance/reports/income-statement`). Komponen ini murni presentasi
 * + drill-down transaksi pembentuk tiap pos.
 */
export default function IncomeStatementReport({ monthFrom, monthTo, canExport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/finance/reports/income-statement", {
        params: { month_from: monthFrom, month_to: monthTo },
      });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memuat Laba Rugi.");
    } finally {
      setLoading(false);
    }
  }, [monthFrom, monthTo]);

  useEffect(() => { load(); }, [load]);

  const dlXlsx = async () => {
    try {
      const res = await api.get("/finance/reports/income-statement.xlsx", {
        params: { month_from: monthFrom, month_to: monthTo }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `laba_rugi_${monthFrom}_${monthTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh XLSX."); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400 text-sm" data-testid="is-loading">Memuat Laba Rugi…</div>;
  if (err) return <div className="p-6 bg-rose-50 border border-rose-200 text-rose-800 text-xs">{err}</div>;
  if (!data) return null;

  const Section = ({ title, total, rows, testid, formatter = (r) => `${r.project_name || "—"} · ${r.invoice_number || r.note || r.week_label || "-"}` }) => {
    const isOpen = !!open[testid];
    return (
      <div className="border-b border-slate-200" data-testid={testid}>
        <button type="button" onClick={() => setOpen((s) => ({ ...s, [testid]: !isOpen }))}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left">
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="text-xs font-semibold text-slate-700 flex-1">{title}</span>
          <span className="text-xs tabular-nums font-bold text-slate-900">{formatIDR(total)}</span>
          <span className="text-[10px] text-slate-400">({(rows || []).length})</span>
        </button>
        {isOpen && rows && rows.length > 0 && (
          <div className="pl-6 pr-3 pb-2 bg-slate-50/50">
            <table className="w-full text-[11px]"><tbody>
              {rows.slice(0, 100).map((r, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-1 text-slate-500 w-24 tabular-nums">{r.date}</td>
                  <td className="py-1 text-slate-700">{formatter(r)}</td>
                  <td className="py-1 text-right tabular-nums text-slate-800 font-semibold">{formatIDR(r.amount)}</td>
                </tr>
              ))}
              {rows.length > 100 && <tr><td colSpan={3} className="py-1 text-center text-[10px] text-slate-400 italic">… {rows.length - 100} baris lainnya (unduh XLSX)</td></tr>}
            </tbody></table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="income-statement-report">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Periode {data.periode.from} s/d {data.periode.to} · basis akrual</div>
        <button type="button" disabled={!canExport} onClick={dlXlsx} data-testid="is-export-xlsx"
          className="h-8 px-3 border border-[#0B1D3A] bg-[#0B1D3A] text-white hover:bg-[#122a4d] text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <Download size={12} /> XLSX
        </button>
      </div>
      <div className="border border-slate-200 bg-white">
        <Section title="Pendapatan Usaha" total={data.pendapatan_usaha.total} rows={data.pendapatan_usaha.rows} testid="is-pendapatan-usaha" />
        <Section title="Pendapatan Klaim Retensi" total={data.pendapatan_klaim_retensi.total} rows={data.pendapatan_klaim_retensi.rows} testid="is-pendapatan-retensi" />
        <div className="px-3 py-2 bg-emerald-50 flex justify-between text-xs font-bold text-emerald-900" data-testid="is-total-pendapatan">
          <span>TOTAL PENDAPATAN</span><span className="tabular-nums">{formatIDR(data.total_pendapatan)}</span>
        </div>
        <Section title="Beban Pokok Proyek — Expense Entries" total={data.beban_pokok.beban_proyek.total} rows={data.beban_pokok.beban_proyek.rows} testid="is-beban-proyek" />
        <Section title="Beban Pokok Proyek — Petty Cash" total={data.beban_pokok.petty_cash_proyek.total} rows={data.beban_pokok.petty_cash_proyek.rows} testid="is-petty-proyek"
          formatter={(r) => `${r.project_name} · ${r.user_name || "—"} · ${r.week_label || ""}`} />
        <div className="px-3 py-2 bg-rose-50 flex justify-between text-xs font-bold text-rose-900">
          <span>Total Beban Pokok</span><span className="tabular-nums">{formatIDR(data.beban_pokok.total)}</span>
        </div>
        <div className="px-3 py-2 bg-sky-50 flex justify-between text-sm font-bold text-sky-900" data-testid="is-laba-kotor">
          <span>Laba Kotor</span><span className="tabular-nums">{formatIDR(data.laba_kotor)}</span>
        </div>
        <Section title="Beban Operasional (Petty Cash non-project)" total={data.beban_operasional.total} rows={data.beban_operasional.rows} testid="is-beban-op"
          formatter={(r) => `${r.user_name || "—"} · ${r.week_label || ""}`} />
        <div className="px-3 py-2 bg-emerald-100 flex justify-between text-sm font-bold text-emerald-900" data-testid="is-laba-op">
          <span>LABA OPERASIONAL</span><span className="tabular-nums">{formatIDR(data.laba_operasional)}</span>
        </div>
        <Section title="PPh Dipotong Klien (informasi arus kas)" total={data.pph_dipotong_informasi.total} rows={data.pph_dipotong_informasi.rows} testid="is-pph" />
        <div className="px-3 py-2 bg-[#0B1D3A] flex justify-between text-sm font-bold text-white" data-testid="is-laba-bersih">
          <span>LABA SEBELUM PAJAK BADAN</span><span className="tabular-nums">{formatIDR(data.laba_sebelum_pajak_badan)}</span>
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 space-y-1" data-testid="is-catatan">
        {data.catatan_akuntansi.map((c, i) => <div key={i}>• {c}</div>)}
      </div>
    </div>
  );
}
